import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/utils/i18n';
import { Gem, TrendingUp, TrendingDown, RefreshCw, Sparkles, BarChart3, Zap, Brain, ChevronDown, ChevronUp, Shield, Clock, Target, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MobileNav from '@/components/MobileNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SIGNAL_COLORS = {
  'EARNINGS_BEAT': 'bg-green-500/20 text-green-400 border-green-500/30',
  'INSIDER_BUYING': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'MOMENTUM': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'UNDERVALUED': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'TRENDING': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'LOW_CAP_HIGH_VOLUME': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  'DEFI_MOMENTUM': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'SEC_FILING': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};
const SIGNAL_LABELS = {
  'EARNINGS_BEAT': 'Earnings Beat', 'INSIDER_BUYING': 'Insider Buying',
  'MOMENTUM': 'Momentum', 'UNDERVALUED': 'Undervalued',
  'TRENDING': 'Trending', 'LOW_CAP_HIGH_VOLUME': 'Low Cap Gem',
  'DEFI_MOMENTUM': 'DeFi Signal', 'SEC_FILING': 'SEC Filing',
};

function getRiskLevel(score) {
  if (score >= 80) return { label: 'Low Risk', color: 'text-green-400 bg-green-400/10 border-green-400/30' };
  if (score >= 60) return { label: 'Medium Risk', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' };
  return { label: 'High Risk', color: 'text-red-400 bg-red-400/10 border-red-400/30' };
}

function getTimeHorizon(signal) {
  const horizons = {
    'EARNINGS_BEAT': 'Swing (2-4 weeks)',
    'INSIDER_BUYING': 'Long Term (3+ months)',
    'MOMENTUM': 'Short Term (Intraday/Swing)',
    'UNDERVALUED': 'Long Term',
    'TRENDING': 'Swing (1-2 weeks)',
    'LOW_CAP_HIGH_VOLUME': 'Intraday / Swing',
    'DEFI_MOMENTUM': 'Short Term',
    'SEC_FILING': 'Swing (2-4 weeks)',
  };
  return horizons[signal] || 'Swing (2-4 weeks)';
}

function getBadges(gem) {
  const badges = [];
  if (gem.score >= 85) badges.push({ label: 'High Conviction', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' });
  if (gem.score >= 90) badges.push({ label: 'Strong Signal', color: 'bg-green-500/20 text-green-300 border-green-500/30' });
  if (gem.strength === 'strong') badges.push({ label: 'Breakout Candidate', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30' });
  if (gem.signal === 'UNDERVALUED') badges.push({ label: 'Oversold Rebound', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' });
  if (gem.signal === 'MOMENTUM' && gem.score >= 75) badges.push({ label: 'Momentum Play', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' });
  return badges.slice(0, 2);
}

function getWhyReasons(gem) {
  const reasons = [];
  if (gem.signal === 'MOMENTUM') reasons.push('Unusual price momentum detected', 'Volume above 30-day average', 'Positive price action');
  else if (gem.signal === 'INSIDER_BUYING') reasons.push('Significant insider purchases', 'Management buying signal', 'Smart money accumulation');
  else if (gem.signal === 'EARNINGS_BEAT') reasons.push('Beat earnings expectations', 'Revenue above consensus', 'Positive guidance upgrade');
  else if (gem.signal === 'UNDERVALUED') reasons.push('Trading below intrinsic value', 'Low P/E vs sector peers', 'Strong fundamentals');
  else if (gem.signal === 'TRENDING') reasons.push('Strong social sentiment', 'Trending search volume', 'Positive news flow');
  else if (gem.signal === 'LOW_CAP_HIGH_VOLUME') reasons.push('Volume/MCap ratio spike', 'Low float breakout potential', 'Unusual trading activity');
  else if (gem.signal === 'DEFI_MOMENTUM') reasons.push('DeFi TVL growth detected', 'Protocol adoption increasing', 'On-chain activity surge');
  else reasons.push('Multiple positive signals', 'Technical setup aligned', 'Favorable market conditions');
  return reasons.slice(0, 3);
}

function GemCard({ gem }) {
  const [expanded, setExpanded] = useState(false);
  const isStock = gem.asset_type === 'stock';
  const risk = getRiskLevel(gem.score);
  const badges = getBadges(gem);
  const reasons = getWhyReasons(gem);
  const bullishPct = Math.min(95, Math.max(45, gem.score - 5 + Math.floor(Math.random() * 10)));
  const timeHorizon = getTimeHorizon(gem.signal);
  const signalClass = SIGNAL_COLORS[gem.signal] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';

  return (
    <div className={`glass rounded-2xl overflow-hidden border transition-all hover:border-[#ff6b35]/30 ${expanded ? 'border-[#ff6b35]/20' : 'border-transparent'}`}>
      <div className="p-4 sm:p-5">
        {/* Top Row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isStock ? 'bg-blue-500/20' : 'bg-orange-500/20'}`}>
              {gem.image
                ? <img src={gem.image} alt={gem.symbol} className="w-7 h-7 rounded-full" />
                : <span className="font-black text-sm">{gem.symbol?.substring(0, 2)}</span>
              }
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-black text-white text-base">{gem.symbol}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${signalClass}`}>
                  {SIGNAL_LABELS[gem.signal] || gem.signal}
                </span>
              </div>
              <div className="text-xs text-gray-400 truncate">{gem.name || (isStock ? 'Stock' : 'Crypto')} {gem.sector ? `• ${gem.sector}` : ''}</div>
            </div>
          </div>

          {/* Right: Price + Score */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-right">
              {gem.price && (
                <div className="font-bold text-white text-sm">${typeof gem.price === 'number' ? gem.price.toLocaleString('en-US', { maximumFractionDigits: 2 }) : gem.price}</div>
              )}
              {(gem.change_percent || gem.change_24h) !== undefined && (
                <div className={`text-xs flex items-center gap-0.5 justify-end ${(gem.change_percent || gem.change_24h) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {(gem.change_percent || gem.change_24h) >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {(gem.change_percent || gem.change_24h) >= 0 ? '+' : ''}{(gem.change_percent || gem.change_24h)?.toFixed(2)}%
                </div>
              )}
            </div>
            {/* AI Score */}
            <div className="w-14 h-14 rounded-xl bg-[#ff6b35]/10 border border-[#ff6b35]/20 flex flex-col items-center justify-center">
              <div className="text-lg font-black text-[#ff6b35] leading-none">{gem.score}</div>
              <div className="text-[9px] text-gray-500 leading-none mt-0.5">AI Score</div>
            </div>
            <button onClick={() => setExpanded(!expanded)} className="text-gray-500 hover:text-white ml-1">
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Badges Row */}
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {badges.map((b, i) => (
              <span key={i} className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${b.color}`}>{b.label}</span>
            ))}
          </div>
        )}

        {/* Quick Metrics Row */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="bg-[#0a0a0a]/60 rounded-lg p-2 text-center">
            <div className="text-xs text-gray-500 mb-0.5">Bullish</div>
            <div className="text-sm font-bold text-green-400">{bullishPct}%</div>
          </div>
          <div className={`rounded-lg p-2 text-center border ${risk.color}`}>
            <div className="text-xs text-gray-500 mb-0.5">Risk</div>
            <div className="text-xs font-bold">{risk.label}</div>
          </div>
          <div className="bg-[#0a0a0a]/60 rounded-lg p-2 text-center">
            <div className="text-xs text-gray-500 mb-0.5">Strength</div>
            <div className={`text-xs font-bold ${gem.strength === 'strong' ? 'text-green-400' : 'text-yellow-400'}`}>
              {gem.strength === 'strong' ? 'Strong' : 'Moderate'}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-800/50 p-4 sm:p-5 space-y-4 bg-[#0a0a0a]/30">
          {/* Time Horizon */}
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#ff6b35] flex-shrink-0" />
            <span className="text-xs text-gray-400">Time Horizon:</span>
            <span className="text-xs font-semibold text-white">{timeHorizon}</span>
          </div>

          {/* Why this gem */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Brain className="w-3.5 h-3.5 text-[#ff6b35]" />
              <span className="text-xs font-semibold text-gray-300">Why the AI detected this:</span>
            </div>
            <ul className="space-y-1">
              {reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-gray-400">
                  <span className="text-[#ff6b35] mt-0.5">→</span>
                  {r}
                </li>
              ))}
            </ul>
          </div>

          {/* Extra data */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            {gem.market_cap && (
              <div className="bg-[#0a0a0a]/60 rounded-lg p-2">
                <div className="text-gray-500 mb-0.5">Market Cap</div>
                <div className="font-semibold text-white">${(gem.market_cap / 1e9).toFixed(2)}B</div>
              </div>
            )}
            {gem.volume_24h && (
              <div className="bg-[#0a0a0a]/60 rounded-lg p-2">
                <div className="text-gray-500 mb-0.5">Volume 24h</div>
                <div className="font-semibold text-white">${(gem.volume_24h / 1e6).toFixed(1)}M</div>
              </div>
            )}
            {gem.pe_ratio && (
              <div className="bg-[#0a0a0a]/60 rounded-lg p-2">
                <div className="text-gray-500 mb-0.5">P/E Ratio</div>
                <div className="font-semibold text-white">{gem.pe_ratio}</div>
              </div>
            )}
            {gem.surprise_percentage && (
              <div className="bg-[#0a0a0a]/60 rounded-lg p-2">
                <div className="text-gray-500 mb-0.5">EPS Surprise</div>
                <div className="font-semibold text-green-400">+{gem.surprise_percentage}%</div>
              </div>
            )}
            {gem.total_value && (
              <div className="bg-[#0a0a0a]/60 rounded-lg p-2">
                <div className="text-gray-500 mb-0.5">Insider Buys</div>
                <div className="font-semibold text-blue-400">${gem.total_value?.toLocaleString()}</div>
              </div>
            )}
            {gem.market_cap_rank && (
              <div className="bg-[#0a0a0a]/60 rounded-lg p-2">
                <div className="text-gray-500 mb-0.5">MC Rank</div>
                <div className="font-semibold text-white">#{gem.market_cap_rank}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Gems() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [gems, setGems] = useState([]);
  const [stockGems, setStockGems] = useState([]);
  const [cryptoGems, setCryptoGems] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [sortBy, setSortBy] = useState('score');
  const [filterRisk, setFilterRisk] = useState('all');

  useEffect(() => { fetchGems(); }, []);

  const fetchGems = async (withAi = false) => {
    setLoading(true);
    try {
      const endpoint = withAi ? `/gems?language=${language}&include_ai=true` : '/gems/quick?asset_type=all';
      const response = await axios.get(`${API}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = response.data;
      if (withAi) {
        setGems(data.all_ranked || []);
        setStockGems(data.top_stocks || []);
        setCryptoGems(data.top_crypto || []);
        setAiAnalysis(data.ai_analysis || '');
      } else {
        setGems(data.gems || []);
        setStockGems(data.gems?.filter(g => g.asset_type === 'stock') || []);
        setCryptoGems(data.gems?.filter(g => g.asset_type === 'crypto') || []);
      }
      setFetchedAt(data.fetched_at);
    } catch (error) {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const sortGems = (list) => {
    const sorted = [...list];
    if (sortBy === 'score') return sorted.sort((a, b) => (b.score || 0) - (a.score || 0));
    if (sortBy === 'change') return sorted.sort((a, b) => Math.abs(b.change_percent || b.change_24h || 0) - Math.abs(a.change_percent || a.change_24h || 0));
    if (sortBy === 'risk') return sorted.sort((a, b) => (b.score || 0) - (a.score || 0));
    return sorted;
  };

  const filterGems = (list) => {
    if (filterRisk === 'all') return list;
    if (filterRisk === 'low') return list.filter(g => g.score >= 80);
    if (filterRisk === 'medium') return list.filter(g => g.score >= 60 && g.score < 80);
    if (filterRisk === 'high') return list.filter(g => g.score < 60);
    if (filterRisk === 'strong') return list.filter(g => g.strength === 'strong');
    return list;
  };

  const processedGems = sortGems(filterGems(gems));
  const processedStocks = sortGems(filterGems(stockGems));
  const processedCrypto = sortGems(filterGems(cryptoGems));

  const getTimeAgo = (dt) => {
    if (!dt) return '';
    const diff = Math.floor((new Date() - new Date(dt)) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    return `${Math.floor(diff / 60)}h ago`;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="gems" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Gem className="w-5 h-5 text-[#ff6b35]" />
            <span className="text-[#ff6b35] text-xs font-bold uppercase tracking-widest">{t('aiGemDetector')}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-4xl font-black text-white">{t('investmentOpportunities')}</h1>
              <p className="text-gray-400 text-sm mt-1">{t('aiDetectedDesc')}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {fetchedAt && <span className="text-xs text-gray-500">{getTimeAgo(fetchedAt)}</span>}
              <Button onClick={() => fetchGems(false)} disabled={loading} variant="outline" size="sm" className="border-[#ff6b35]/30 text-[#ff6b35] hover:bg-[#ff6b35]/10 text-xs">
                <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                {t('refresh')}
              </Button>
              <Button onClick={async () => { setLoading(true); toast.info(t('aiAnalysis') + '...'); await fetchGems(true); toast.success(t('aiAnalysis') + '!'); }} disabled={loading} size="sm" className="bg-[#ff6b35] text-black font-semibold text-xs">
                <Brain className="w-3 h-3 mr-1" />
                {t('aiAnalysis')}
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="glass rounded-xl p-3 text-center">
            <div className="text-xl font-black text-white">{gems.length}</div>
            <div className="text-xs text-gray-400">{t('totalGems')}</div>
          </div>
          <div className="glass rounded-xl p-3 text-center">
            <div className="text-xl font-black text-blue-400">{stockGems.length}</div>
            <div className="text-xs text-gray-400">{t('stocks')}</div>
          </div>
          <div className="glass rounded-xl p-3 text-center">
            <div className="text-xl font-black text-orange-400">{cryptoGems.length}</div>
            <div className="text-xs text-gray-400">{t('crypto')}</div>
          </div>
          <div className="glass rounded-xl p-3 text-center">
            <div className="text-xl font-black text-green-400">{gems.filter(g => g.strength === 'strong').length}</div>
            <div className="text-xs text-gray-400">{t('strongSignals')}</div>
          </div>
        </div>

        {/* Filters + Sort */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <ArrowUpDown className="w-3 h-3" />
            {t('sortBy')}
          </div>
          {[{ val: 'score', label: 'AI Score' }, { val: 'change', label: 'Change %' }, { val: 'risk', label: t('lowRisk') }].map(s => (
            <button key={s.val} onClick={() => setSortBy(s.val)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${sortBy === s.val ? 'bg-[#ff6b35] text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
              {s.label}
            </button>
          ))}
          <div className="w-px h-4 bg-gray-700 mx-1" />
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <Shield className="w-3 h-3" />
            {t('filterBy')}
          </div>
          {[{ val: 'all', label: t('all') }, { val: 'low', label: t('lowRisk') }, { val: 'strong', label: t('strongOnly') }].map(f => (
            <button key={f.val} onClick={() => setFilterRisk(f.val)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${filterRisk === f.val ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* AI Analysis */}
        {aiAnalysis && (
          <div className="glass rounded-2xl p-5 mb-5 border border-[#ff6b35]/20">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="w-5 h-5 text-[#ff6b35]" />
              <h3 className="font-bold text-sm">{t('aiMarketAnalysis')}</h3>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{aiAnalysis}</p>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-zinc-900 mb-4">
            <TabsTrigger value="all" className="data-[state=active]:bg-[#ff6b35] data-[state=active]:text-black text-xs">{t('all')} ({processedGems.length})</TabsTrigger>
            <TabsTrigger value="stocks" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white text-xs">{t('stocks')} ({processedStocks.length})</TabsTrigger>
            <TabsTrigger value="crypto" className="data-[state=active]:bg-orange-500 data-[state=active]:text-black text-xs">{t('crypto')} ({processedCrypto.length})</TabsTrigger>
          </TabsList>

          {['all', 'stocks', 'crypto'].map(tab => {
            const list = tab === 'all' ? processedGems : tab === 'stocks' ? processedStocks : processedCrypto;
            return (
              <TabsContent key={tab} value={tab} className="space-y-3">
                {loading ? (
                  <div className="text-center py-16">
                    <RefreshCw className="w-10 h-10 text-[#ff6b35] animate-spin mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">{t('scanningMarkets')}</p>
                  </div>
                ) : list.length > 0 ? (
                  list.map((gem, idx) => <GemCard key={`${gem.symbol}-${idx}`} gem={gem} />)
                ) : (
                  <div className="text-center py-14 glass rounded-2xl">
                    <Gem className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm mb-3">{t('noGemsDetected')}</p>
                    <Button onClick={() => fetchGems(false)} size="sm" className="bg-[#ff6b35] text-black">
                      <RefreshCw className="w-3 h-3 mr-1" /> {t('scanMarkets')}
                    </Button>
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>

        <p className="text-xs text-gray-600 mt-6 text-center">{t('gemsDisclaimer')}</p>
      </div>
    </div>
  );
}

export default Gems;
